/**
 * Table tools — sortable column headers and a text filter, for tables whose rows are IN HAND.
 *
 * Promoted from isg-sales `components/sort-header.js` (2026-10-05) on 2026-10-26, when isg-bi and
 * isg-marketing needed the same thing (Callum: "filtering/reordering capabilities on all the tables
 * … ordering by date/size all that kinda stuff"). Until then ui-kit had no table-sort component,
 * which is exactly why five apps had grown their own; this is the one copy.
 *
 *   import { sortState, sortTh, sortArrow, wireSort, sortRows, filterRows, filterInput, wireFilter } from '@isg/ui-kit';
 *
 *   const sort = sortState('value', 'desc');                    // per table, kept by the page
 *   `<th ${sortTh(sort, 'value', { right: true })}>Value ${sortArrow(sort, 'value')}</th>`
 *   wireSort(tableEl, sort, () => repaint());                   // after every render of the table
 *
 *   host.innerHTML = filterInput({ placeholder: 'Filter…' }) + '<div data-list></div>';   // ONCE per tab
 *   wireFilter(host.querySelector('[data-table-filter]'), q => { state.q = q; repaint(); });
 *   rows = sortRows(filterRows(rows, state.q), sort);           // in repaint()
 *
 * Rules, each from a table that got them wrong first:
 * - The faint ↕ on every unsorted column is what tells a reader the table sorts at all.
 * - Clicking the sorted column reverses it; a new column starts at its natural direction —
 *   numbers and dates descending (biggest / newest first), text A–Z (`text: true`).
 * - Headers are buttons (role, tabindex, Enter/Space): a header only a mouse can use is not a control.
 * - The filter input is rendered ONCE and only the list repaints — re-rendering the input on each
 *   keystroke loses the caret. `wireFilter` debounces and never re-renders anything itself.
 * - A SERVER-PAGED or capped list must sort on the server before the cut: sorting the page in hand is
 *   "largest of these 50". These helpers are for lists the page holds whole, or holds with the cut
 *   stated on screen ("the 500 best-fit of 3,210").
 * - 🔴 No visible strings live here (except "Clear", the estate's own word on every bar). Placeholders and labels come from the app, so an i18n-adopted
 *   app passes `t('…')` and this file stays catalogue-free.
 */

import { getIcon } from './navigation.js';

export function sortState(key, dir = 'desc') {
  return { key, dir };
}

export function sortArrow(state, key) {
  if (state.key !== key) return '<span style="opacity:0.3">&#8597;</span>';
  return state.dir === 'asc' ? '&#9650;' : '&#9660;';
}

/** The attributes for a sortable `<th>`. `text: true` = starts A–Z; `right` = numeric column. */
export function sortTh(state, key, { right = false, text = false, title = '' } = {}) {
  const sorted = state.key === key;
  const aria = sorted ? (state.dir === 'asc' ? 'ascending' : 'descending') : 'none';
  return `role="button" tabindex="0" data-sort="${key}" data-sort-text="${text ? 1 : 0}" `
    + `aria-sort="${aria}"${title ? ` title="${String(title).replace(/"/g, '&quot;')}"` : ''} `
    + `style="cursor:pointer;user-select:none;white-space:nowrap${right ? ';text-align:right' : ''}"`;
}

/** Wire every `th[data-sort]` under `root`; `onChange(state)` after the state changes. */
export function wireSort(root, state, onChange) {
  if (!root) return;
  root.querySelectorAll('th[data-sort]').forEach(th => {
    const go = () => {
      const key = th.dataset.sort;
      if (state.key === key) {
        state.dir = state.dir === 'asc' ? 'desc' : 'asc';
      } else {
        state.key = key;
        state.dir = th.dataset.sortText === '1' ? 'asc' : 'desc';
      }
      onChange(state);
    };
    th.addEventListener('click', go);
    th.addEventListener('keydown', e => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); }
    });
  });
}

/** Sort rows in place by `state` — nulls last whichever way, numbers as numbers, text with numeric collation. */
export function sortRows(rows, state, get = (r, k) => r[k]) {
  const m = state.dir === 'asc' ? 1 : -1;
  return rows.sort((a, b) => {
    const x = get(a, state.key), y = get(b, state.key);
    const xn = x == null || x === '', yn = y == null || y === '';
    if (xn || yn) return xn === yn ? 0 : (xn ? 1 : -1);
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * m;
    return String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' }) * m;
  });
}

// ── filtering ───────────────────────────────────────────────────────────────

/** Lower-case, accents folded, so "skoda" finds "ŠKODA" and "muller" finds "Müller". */
export function foldText(s) {
  return String(s == null ? '' : s).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

function rowText(row) {
  if (row == null) return '';
  if (typeof row !== 'object') return String(row);
  return Object.values(row).map(v => {
    if (v == null) return '';
    if (Array.isArray(v)) return v.map(x => (typeof x === 'object' && x ? Object.values(x).join(' ') : String(x))).join(' ');
    if (typeof v === 'object') return Object.values(v).join(' ');
    return String(v);
  }).join(' ');
}

/**
 * Keep the rows matching every word of `query` (any order, case- and accent-insensitive).
 * `get(row)` returns the text to search — defaults to every value on the row, one level deep.
 * An empty query returns the rows untouched (the same array).
 */
export function filterRows(rows, query, get = rowText) {
  const words = foldText(query).split(/\s+/).filter(Boolean);
  if (!words.length) return rows;
  return rows.filter(r => {
    const text = foldText(get(r));
    return words.every(w => text.includes(w));
  });
}

// ── the estate filter bar ───────────────────────────────────────────────────
//
// The shape every list in the estate already uses (BI Directors, Purchasing's worklist, Sales'
// account list): ONE `.filter-bar` row — a search box with the magnifier (`.search-input-wrapper`),
// a `.form-select` per facet whose first option is "All …", and a Clear button once anything is set.
// Callum, 2026-10-26: the first cut (a bare text box above each table) "isnt the same as the rest of
// the estate". Drawn ONCE; only the list under it repaints, so typing never loses the caret.
//
//   const f = filterState();                                              // { q: '', facets: {} }
//   host.innerHTML = filterBar(f, { placeholder: 'Search accounts…', facets: [
//     { key: 'country', all: 'All countries', options: facetOptions(rows, r => r.country) } ] });
//   wireFilterBar(host.querySelector('[data-filter-bar]'), f, () => repaint());
//   rows = applyFilters(rows, f, { text: r => …, facets: { country: r => r.country } });
//   setFilterCount(host, shown, total, 'accounts');

export function filterState() {
  return { q: '', facets: {} };
}

/** Distinct values of `get(row)` (arrays spread) with how many rows carry each, most common first. */
export function facetOptions(rows, get, { label = v => v, max = 200 } = {}) {
  const counts = new Map();
  for (const r of rows || []) {
    const v = get(r);
    for (const x of Array.isArray(v) ? v : [v]) {
      if (x == null || x === '') continue;
      counts.set(String(x), (counts.get(String(x)) || 0) + 1);
    }
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, max)
    .map(([value, count]) => ({ value, label: label(value), count }));
}

const _q = s => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/**
 * The filter bar's markup. `facets`: [{ key, all, options: [{value, label, count}] }] — a facet with
 * fewer than two options is left out (a dropdown with one choice filters nothing). `extra` is any
 * further control the page owns (a period select…), placed before Clear.
 */
export function filterBar(state, { placeholder = '', facets = [], extra = '', searchWidth = '300px', className = '' } = {}) {
  const shown = facets.filter(f => (f.options || []).length > 1 || state.facets[f.key]);
  const any = Boolean(state.q) || Object.values(state.facets).some(Boolean);
  return `<div class="filter-bar ${className}" data-filter-bar>
    <div class="search-input-wrapper" style="flex:1 1 220px;max-width:${searchWidth}">
      <span class="search-icon">${getIcon('search', 16)}</span>
      <input type="search" class="search-input" data-filter-q autocomplete="off"
        placeholder="${_q(placeholder)}" aria-label="${_q(placeholder)}" value="${_q(state.q)}">
    </div>
    ${shown.map(f => `<select class="form-select" data-filter-facet="${_q(f.key)}" aria-label="${_q(f.all)}">
      <option value="">${_q(f.all)}</option>
      ${f.options.map(o => `<option value="${_q(o.value)}"${String(state.facets[f.key] || '') === o.value ? ' selected' : ''}>${_q(o.label)}${o.count != null ? ` (${o.count})` : ''}</option>`).join('')}
    </select>`).join('')}
    ${extra}
    <button type="button" class="btn btn-ghost btn-sm" data-filter-clear style="${any ? '' : 'display:none'}">${getIcon('x', 14)} Clear</button>
    <span data-filter-count style="margin-left:auto;color:var(--text-secondary);font-size:var(--text-sm);white-space:nowrap"></span>
  </div>`;
}

/** Wire a bar drawn by `filterBar`. `onChange(state)` after every change; the bar itself is never redrawn. */
export function wireFilterBar(bar, state, onChange, { delay = 150 } = {}) {
  if (!bar) return;
  const clear = bar.querySelector('[data-filter-clear]');
  const sync = () => {
    if (clear) clear.style.display = (state.q || Object.values(state.facets).some(Boolean)) ? '' : 'none';
    onChange(state);
  };
  const input = bar.querySelector('[data-filter-q]');
  let timer = null;
  if (input) {
    input.addEventListener('input', () => {
      if (timer) clearTimeout(timer);
      const fire = () => { timer = null; state.q = input.value; sync(); };
      if (!input.value) fire(); else timer = setTimeout(fire, delay);
    });
  }
  bar.querySelectorAll('[data-filter-facet]').forEach(sel => {
    sel.addEventListener('change', () => { state.facets[sel.dataset.filterFacet] = sel.value; sync(); });
  });
  if (clear) {
    clear.addEventListener('click', () => {
      state.q = ''; state.facets = {};
      if (input) input.value = '';
      bar.querySelectorAll('[data-filter-facet]').forEach(sel => { sel.value = ''; });
      sync();
    });
  }
}

/** Rows matching the search (every word, any order, accent-blind) AND every chosen facet. */
export function applyFilters(rows, state, { text = rowText, facets = {} } = {}) {
  let out = filterRows(rows, state.q, text);
  for (const [key, value] of Object.entries(state.facets)) {
    if (!value || !facets[key]) continue;
    out = out.filter(r => {
      const v = facets[key](r);
      return Array.isArray(v) ? v.map(String).includes(value) : String(v ?? '') === value;
    });
  }
  return out;
}

/** "12 of 274 accounts" in the bar's count slot (the whole number when nothing is filtered). */
export function setFilterCount(root, shown, total, what = '') {
  const el = root && root.querySelector('[data-filter-count]');
  if (!el) return;
  const fmt = n => Number(n).toLocaleString();
  el.textContent = total ? `${shown === total ? fmt(total) : `${fmt(shown)} of ${fmt(total)}`}${what ? ` ${what}` : ''}` : '';
}

/** A bare filter box (no bar). Prefer `filterBar` — kept for a list too small for facets. */
export function filterInput({ placeholder = '', value = '', id = '', width = '300px', extra = '' } = {}) {
  const attrs = [
    'type="search"', 'class="form-input"', 'data-table-filter', 'autocomplete="off"',
    id ? `id="${id}"` : '',
    `placeholder="${String(placeholder).replace(/"/g, '&quot;')}"`,
    `value="${String(value).replace(/"/g, '&quot;')}"`,
    `aria-label="${String(placeholder).replace(/"/g, '&quot;')}"`,
    `style="max-width:${width};min-width:0"`,
    extra,
  ].filter(Boolean).join(' ');
  return `<input ${attrs}>`;
}

/** Call `onChange(query)` as the person types (debounced), and at once on clear. Never re-renders the input. */
export function wireFilter(input, onChange, { delay = 150 } = {}) {
  if (!input) return;
  let timer = null;
  const fire = () => { timer = null; onChange(input.value); };
  input.addEventListener('input', () => {
    if (timer) clearTimeout(timer);
    if (!input.value) { fire(); return; }
    timer = setTimeout(fire, delay);
  });
  input.addEventListener('search', () => { if (timer) clearTimeout(timer); fire(); });
}

/** "12 of 274" — the one line every filtered table should carry, so a short list is not read as a short world. */
export function filterCount(shown, total, { of = 'of' } = {}) {
  return shown === total ? String(total) : `${shown} ${of} ${total}`;
}
